import { Link as RouterLink } from "@tanstack/react-router";
import { ArrowRight, Radio } from "lucide-react";

import { LineUp } from "@/components/home/line-up";
import { Image } from "@/components/image";
import { Link } from "@/components/link";
import type { OnAirEpisode } from "@/lib/episodes";
import { cn, formatTimeToHHMM } from "@/lib/utils";

const localDateString = (date: Date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const todayExploreSearch = () => {
  const today = localDateString(new Date());
  return { from: today, to: today };
};

function EpisodeArt({ episode }: { episode: OnAirEpisode }) {
  return (
    <span className="relative block size-10 shrink-0 overflow-hidden rounded-lg bg-white/10">
      {episode.iconUrl ? (
        <Image
          fill
          alt={`${episode.show.name} artwork`}
          className="object-cover"
          src={episode.iconUrl}
        />
      ) : (
        <span className="flex size-full items-center justify-center">
          <Radio className="size-5 opacity-70" />
        </span>
      )}
    </span>
  );
}

export function UpNextPanel(props: { episodes: OnAirEpisode[] }) {
  const now = new Date();

  return (
    <aside className="flex min-w-0 flex-col rounded-3xl lg:max-h-[26rem] bg-white/10 p-6 backdrop-blur-sm">
      <h2 className="text-xl font-bold tracking-tight">Coming up today</h2>

      {props.episodes.length === 0 ? (
        <p className="text-primary-foreground/70 mt-4 text-sm">
          Nothing scheduled today.
        </p>
      ) : (
        <ul className="mt-4 flex-1 divide-y divide-white/15 lg:overflow-y-auto lg:pe-1">
          {props.episodes.map((episode) => {
            const finished = new Date(episode.endOfEpisode) < now;

            return (
              <li key={episode.id}>
                <Link
                  className={cn(
                    "group flex items-start gap-3 rounded-lg py-3.5 sm:items-center sm:gap-4 transition-colors hover:bg-white/5",
                    finished && "opacity-45"
                  )}
                  href={`/view/shows/${episode.show.id}`}
                >
                  <span className="text-primary-foreground/80 hidden w-12 shrink-0 tabular-nums sm:block">
                    {formatTimeToHHMM(new Date(episode.startOfEpisode))}
                  </span>
                  <EpisodeArt episode={episode} />
                  <span className="min-w-0 flex-1">
                    <span className="text-primary-foreground/80 block text-sm tabular-nums sm:hidden">
                      {formatTimeToHHMM(new Date(episode.startOfEpisode))}
                    </span>
                    <span
                      className={cn(
                        "block truncate font-semibold group-hover:underline",
                        episode.cancelled && "line-through"
                      )}
                    >
                      {episode.title}
                    </span>
                    <span className="text-primary-foreground/70 block truncate text-sm">
                      {episode.show.name}
                    </span>
                    <LineUp
                      className="text-primary-foreground/80"
                      hosts={episode.hosts}
                      techies={episode.techies}
                    />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <RouterLink
        className="text-primary-foreground/80 hover:text-primary-foreground mt-4 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
        search={todayExploreSearch()}
        to="/explore"
      >
        See all of today in Explore <ArrowRight className="size-4" />
      </RouterLink>
    </aside>
  );
}
